import { TProject } from '@ws/shared/types';
import { useEffect, useRef } from 'react';
// eslint-disable-next-line import/no-named-as-default
import { DndProvider } from 'react-dnd';
import MultiBackend from 'react-dnd-multi-backend';
import HTML5toTouch from 'react-dnd-multi-backend/dist/esm/HTML5toTouch';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ContentWindowsKeeper, WindowTabsPanel } from '../../components';
import { Page } from '../../components/Page';
import { TextatorSplashScreen } from '../../components/TextatorSplashScreen';
import { NOT_FOUND, PROJECTS_ROUTE } from '../../constants';
import { EditabilityContextManager } from '../../context/EditabilityContext';
import { TAppState } from '../../redux';
import { applyEvents, setProject, setSelectedNode } from '../../redux/editor';
import { iDBProjectService } from '../../resources/iDBProject';
import { getEntityRef } from '../../utils/getEntityRef';
import { projectBuilder, projectProcessor } from '../../utils/projectProcessing';
import { rescueNodes } from '../../utils/projectStructure/repairProjectStructure';
import { getProjectRoute } from '../../utils/routes';

import styles from './EditorPage.module.css';
import { PlayGround } from './PlayGround';
import { useLeaveEditorPage } from './useLeaveEditorPage';
import { Sidebar } from './Sidebar';
import { SplitManager } from './SplitManager';

export function EditorPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId, nodeId } = useParams<{ projectId: string; nodeId: string }>();

  const { project, selected } = useSelector((state: TAppState) => state.editor);

  // Примитивы, а не сам объект: ссылка на selected меняется на каждый SELECT_NODE,
  // и эффект бы перезапускался вхолостую.
  const selectedId = selected?.id;
  const selectedType = selected?.type;

  // Уход со страницы любым путём: барьер сохранения + очистка проекта (WSL-14).
  useLeaveEditorPage();

  // Загрузчик ниже асинхронный, и продолжать после await имеет право ровно ОДИН его прогон —
  // последний, на всё ещё живой странице. Поэтому два механизма, у каждого своя работа:
  //
  // 1. Флаг жизни страницы — от ответа, пришедшего после НАСТОЯЩЕГО ухода: поздний setProject
  //    заново открыл бы sync-сессию уже на другой странице. true выставляется В ТЕЛЕ эффекта,
  //    а не только начальным значением ref: StrictMode в dev прогоняет mount → cleanup → mount,
  //    ref переживает симулированный размонтаж, и без повторного true флаг оставался ложным
  //    на живой странице — ни один проект не открывался (WSL-19).
  //
  // 2. Поколение прогона — от ДВУХ живых прогонов. Тот же реплей StrictMode запускает
  //    загрузчик дважды, и без поколения до setProject доходили обе ветви: два PROJECT_SET,
  //    две sync-сессии и дважды применённые события восстановления.
  //
  // Поколение сбрасывает и перезапуск по смене deps (в deps есть project). Для ветки
  // projectId === 'new' это опасно: прерванный прогон уже создал проект, а следующий создал
  // бы второй. Поэтому создание однократное на время полёта — все прогоны, пришедшие, пока
  // оно не завершилось, ждут один и тот же промис.
  //
  // Известный остаток (предсуществующий, вне этой правки): синхронные ветки загрузчика —
  // выбор узла и фоллбэк-навигации — под StrictMode тоже выполняются дважды. Они
  // идемпотентны, кроме navigate(PROJECTS_ROUTE) без replace при пустом projectId.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loaderRunRef = useRef(0);
  const pendingNewProjectRef = useRef<Promise<TProject> | null>(null);

  useEffect(() => {
    loaderRunRef.current += 1;
    const run = loaderRunRef.current;
    const isStale = () => !isMountedRef.current || run !== loaderRunRef.current;

    const check = async () => {
      try {
        if (!projectId) {
          navigate(PROJECTS_ROUTE);
          return;
        }

        if (projectId !== project?.id) {
          if (projectId === 'new') {
            if (!pendingNewProjectRef.current) {
              const creation = (async () => {
                const created = projectBuilder.createProject({});
                await iDBProjectService.saveOne(created);
                return created;
              })();

              // Однократность живёт ровно столько, сколько летит СВОЁ создание: по
              // завершении ref освобождается — но только если в нём всё ещё этот промис.
              // Чистить в продолжении прогона нельзя: устаревший прогон туда не доходит,
              // и завершённый промис достался бы следующему, независимому заходу.
              // Двухаргументный then, а не finally: finally на отвергнутом промисе
              // породил бы необработанный rejection.
              const release = () => {
                if (pendingNewProjectRef.current === creation) {
                  pendingNewProjectRef.current = null;
                }
              };
              creation.then(release, release);

              pendingNewProjectRef.current = creation;
            }

            const newProject = await pendingNewProjectRef.current;
            if (isStale()) {
              return;
            }

            dispatch(setProject({ project: newProject }));
            navigate(getProjectRoute(newProject.id, nodeId));

            return;
          }

          const storedProject = await iDBProjectService.getOneById(projectId);
          if (isStale()) {
            return;
          }

          const processedProject = projectProcessor.process(storedProject);

          if (projectProcessor.hasError(processedProject)) {
            toast.error(`Can't open this project`);
            navigate(NOT_FOUND);
            return;
          }

          const { value: validProject } = processedProject;

          const lostEvents = rescueNodes(validProject);

          dispatch(setProject({ project: validProject }));

          if (lostEvents.acts.length > 0) {
            console.log('APPLY LOST', lostEvents);
            dispatch(applyEvents(lostEvents));
          }
          return;
        }

        if (!project) {
          return;
        }

        if (selectedId !== nodeId && nodeId) {
          const selectedNode = project.collections[nodeId] || project.notes[nodeId];

          if (selectedNode) {
            dispatch(setSelectedNode({ selected: getEntityRef(selectedNode) }));
            return;
          }

          // Узла из адреса в проекте нет: он мог быть удалён на другом устройстве
          // или адрес введён руками. Молчать нельзя — selected остался бы null,
          // и экран навсегда залип бы на сплеш-скрине.
          navigate(getProjectRoute(projectId, project.root), { replace: true });
          return;
        }

        // Тот же тупик с другой стороны: selected есть, но узел под ним исчез
        // (например, пришла delta с удалением). Рендер-гард ниже показал бы сплеш.
        if (selectedId && selectedType && !project[`${selectedType}s`][selectedId]) {
          navigate(getProjectRoute(projectId, project.root), { replace: true });
        }
      } catch (error) {
        if (isStale()) {
          return;
        }

        console.error('Error in EditorPage check:', error);
        toast.error('Failed to load project');
        navigate(PROJECTS_ROUTE);
      }
    };

    check();
  }, [projectId, nodeId, dispatch, project, selectedId, selectedType, navigate]);

  if (!project || !selected || !project[`${selected.type}s`][selected.id]) {
    return <TextatorSplashScreen />;
  }

  return (
    <EditabilityContextManager>
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <Page className={styles['editor-page']}>
          <Sidebar />

          <div className={styles['editor-page__group-wrapper']}>
            <SplitManager>
              <PlayGround />
            </SplitManager>

            <WindowTabsPanel />
            <ContentWindowsKeeper />
          </div>
        </Page>
      </DndProvider>
    </EditabilityContextManager>
  );
}
